<template>
  <label
    class="input-custom-checkbox"
    :class="['input-custom-checkbox-' + size, 'text-' + size]"
  >
    <input
      class="hidden"
      type="checkbox"
      :checked="currentValue"
      :disabled="readonly"
      @change="updateValue"
    />
    <div
      class="input-custom-checkbox-box"
      :class="[{ checked: currentValue, 'input-custom-checkbox-readonly': readonly }]"
      :style="
        checkboxColor && currentValue
          ? 'background-color: var(--' + checkboxColor + '); border-color: var(--' + checkboxColor + ');'
          : ''
      "
    ></div>
    <div
      v-if="label || description"
      class="select-none ml-3 checkbox-text"
      :class="{
        'leading-5': size === 'sm' || size === 'md',
        'leading-6': size === 'lg',
        'leading-7': size === 'xl'
      }"
    >
      <div
        v-if="label"
        class="flex gap-4 items-center"
      >
        <div class="font-medium">{{ label }}</div>
      </div>
      <slot
        v-else
        name="label"
      ></slot>
      <div
        v-if="description"
        class="text-tertiary max-w-[500px]"
      >
        {{ description }}
      </div>
      <slot
        v-else
        name="description"
      ></slot>
    </div>
  </label>
</template>

<script setup lang="ts">
import { computed } from 'vue'
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: null
  },
  isChecked: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: null
  },
  description: {
    type: String,
    default: null
  },
  size: {
    type: String,
    default: 'md'
  },
  checkboxColor: {
    type: String,
    default: null
  },
  noMargin: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['update:modelValue', 'change'])
const currentValue = computed(() => {
  if (props.modelValue !== null) {
    return props.modelValue
  } else {
    return props.isChecked
  }
})

const updateValue = () => {
  if (props.modelValue !== null) {
    emit('update:modelValue', !props.modelValue)
  }
  emit('change', !props.isChecked)
}
</script>

<style>
.input-custom-checkbox {
  cursor: pointer;
  display: flex;
  align-items: flex-start;
}
.input-custom-checkbox-box {
  flex-shrink: 0;
  border: 1px solid var(--border-primary);
  border-radius: var(--rounded-sm);
  display: inline-block;
}
.input-custom-checkbox-sm .input-custom-checkbox-box {
  height: 16px;
  width: 16px;
}
.input-custom-checkbox-md .input-custom-checkbox-box {
  height: 16px;
  width: 16px;
}
.input-custom-checkbox-lg .input-custom-checkbox-box {
  height: 20px;
  width: 20px;
}
.input-custom-checkbox-xl .input-custom-checkbox-box {
  height: 24px;
  width: 24px;
}
.input-custom-checkbox-box.checked {
  background-color: var(--fg-brand);
  border-color: var(--fg-brand);
  position: relative;
}
.input-custom-checkbox-box.checked::after {
  font-family: 'Font Awesome 7 Pro';
  content: '\f00c';
  font-weight: 900;
  color: #fff;
  position: absolute;
}

.input-custom-checkbox-readonly,
.input-custom-checkbox-box.checked.input-custom-checkbox-readonly {
  cursor: not-allowed;
  background-color: var(--bg-disabled);
  border-color: var(--border-disabled);
}
.input-custom-checkbox-box.checked.input-custom-checkbox-readonly::after {
  color: var(--fg-disabled);
}

.input-custom-checkbox-sm .checked::after {
  font-size: var(--text-xs);
  top: 0;
  left: 3px;
}
.input-custom-checkbox-md .checked::after {
  font-size: var(--text-xs);
  top: 0;
  left: 3px;
}
.input-custom-checkbox-lg .checked::after {
  font-size: var(--text-sm);
  top: 1px;
  left: 4px;
}
.input-custom-checkbox-xl .checked::after {
  font-size: var(--text-md);
  top: 2px;
  left: 6px;
}

.horizontal-text .checkbox-text {
  display: flex;
  align-items: start;
  gap: var(--s-2);
}
</style>
